import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useChat, type TtsStreamController } from "../hooks/useChat";
import { useTts } from "../hooks/useTts";
import { useAssistantProfile } from "../hooks/useAssistantProfile";

type ConversationApi = ReturnType<typeof useChat>;

const Ctx = createContext<ConversationApi | null>(null);

/**
 * Holds the single, app-wide assistant conversation. Mounted once in the Shell
 * so the Intelligence Hub route and the Mochi popover render the SAME live
 * state (continuity), and a chat started anywhere keeps streaming across
 * navigation. Session history still comes from the server (sessionApi), so it
 * is inherently shared regardless.
 *
 * The TTS controller is wired in HERE (not at the route) so the single shared
 * useChat streams replies to the speech engine when the user has auto-speak on,
 * regardless of which surface (Hub route or Mochi popover) is mounted. Deps are
 * the stable useCallback-memoized methods off useTts(), not the `voice` object
 * itself — useTts() returns a fresh object literal every render, which would
 * defeat this memo (and recreate chat.sendMessage/newChat every render) if we
 * depended on `voice` directly.
 */
export function AssistantConversationProvider({ children }: { children: ReactNode }) {
  const voice = useTts();
  const { data: profile } = useAssistantProfile();
  const ttsController = useMemo<TtsStreamController>(
    () => ({
      onAssistantStart: (id: string) => {
        if (profile?.tts_auto_speak) voice.beginLive(id);
      },
      onAssistantToken: (_id: string, delta: string) => {
        if (profile?.tts_auto_speak) voice.feedLive(delta);
      },
      onAssistantDone: () => {
        if (profile?.tts_auto_speak) voice.endLive();
      },
      onAbort: () => voice.stop(),
    }),
    [profile?.tts_auto_speak, voice.beginLive, voice.feedLive, voice.endLive, voice.stop],
  );
  const chat = useChat({ tts: ttsController });
  return <Ctx.Provider value={chat}>{children}</Ctx.Provider>;
}

export function useAssistantConversation(): ConversationApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAssistantConversation must be used within <AssistantConversationProvider>");
  return ctx;
}
